<template>
  <form
    class="flex flex-col gap-sm pb-sm"
    id="anstalld-form"
    @submit="attemptSubmit"
  >
    <FormGroup>
      <KontaktorsakInput
        v-model:kontaktorsakId="kontaktorsakId"
        v-model:fortydligadKontaktorsak="fortydligadKontaktorsak"
        :selectedSamtalsamneIds="values.samtalsamneIds || []"
        :disabled="props.arende && !!props.arende.kontaktorsakId"
        @push="pushSamtalsamne"
        @remove="removeSamtalsamne"
      />
    </FormGroup>
    <FormGroup>
      <InternaKommentarer
        v-model="internKommentar"
        :required="shouldRequireInternKommentar(values.lamnaOverTill)"
        :internaKommentarer="props.arende?.internaKommentarer ?? []"
        :show-verified="true"
        :can-edit="false"
      />
    </FormGroup>
    <FormGroup>
      <SakraMeddelanden
        v-model="sakraMeddelanden"
        :showEmailIsMissingWarning="showEmailIsMissingWarning"
        :sakraMeddelanden="props.arende?.sakraMeddelanden ?? []"
        :shouldUseDefaultTemplate="shouldUseDefaultTemplate"
        @template-unchanged="onTemplateUnchanged"
      />
    </FormGroup>
    <FormGroup>
      <KanalInput
        v-model="kanal"
        required
        :disabled="!!props.arende"
        :options="AnstalldKanalOptions"
      />
      <KundgruppInput
        v-model="kundgrupp"
        :disabled="!!props.arende"
        :options="AnstalldKundgruppOptions"
      />
      <OmbudInput v-model="ombud" :disabled="!!props.arende" />
    </FormGroup>
    <CollapsableFormGroup id="hantering" title="Hantering">
      <LamnaOverInput
        v-model="lamnaOverTill"
        :options="AnstalldLamnaOverTillOptionGroups"
      />
      <FormGroup class="my-sm">
          <avtalat-label
            label="Kontaktuppgifter"
            help-text="Vänlig fyll minst en kontaktuppgift vid överlämning till andra tjänster"
            :required="shouldRequireAtLeastOneContact(lamnaOverTill)"
          />
          <EmailInput
            v-model="email"
            label="Kundens e-postadress"
            :required="false"
          />
          <TelefonnummerInput
            v-model="telefonnummer"
            label="Kundens telefonnummer"
          />
          <p
              v-if="showAtLeastOneContactMissingWarning"
              class="text-md font-bold text-[--feedback-alert-darker]"
              data-testid="form-error-at-least-one-contact"
              >Ange minst en kontaktuppgift</p
            >
      </FormGroup>
      <div class="flex flex-col gap-none py-xs" v-if="props.arende?.id">
        <label for="copy-arende-url">Kopiera länk till ärende</label>
        <CopyToClipboard
          id="copy-arende-url"
          text="Klicka här för att kopiera ärendelänk"
          :data="composeAnstalldarendeUrl(props.arende.id)"
        />
      </div>
      <ArendetypInput v-model="arendetyp" />
    </CollapsableFormGroup>
    <avtalat-message v-if="arendeStore.arende.error" type="negative" small
      ><p>
        {{
          arendeStore.arende.error.errors ??
          "Något har gått fel, kontakta IT-drift."
        }}
      </p></avtalat-message
    >
    <div class="flex flex-row justify-end gap-xs">
      <avtalat-button
        id="save-button"
        v-if="saveButtonIsVisible"
        outlined
        :disabled="!meta.valid"
        :loading="isSubmitting"
        @click="setArendeCloseStatus(false)"
        >Spara</avtalat-button
      >
      <avtalat-button
        id="save-close-button"
        v-if="saveCloseButtonIsVisible"
        :disabled="!meta.valid"
        :loading="isSubmitting"
        @click="setArendeCloseStatus(true)"
        >Spara och stäng</avtalat-button
      >
    </div>
  </form>
  <SubmissionWarningDialog
    v-if="showSubmissionWarningDialog"
    :showUnchangedDefaultMessageWarning="showUnchangedDefaultMessageWarning"
    :showEmailMissingWarning="showEmailMissingWarning"
    @submit="onSubmit"
    @cancel="() => (showSubmissionWarningDialog = false)"
  />
</template>

<script setup lang="ts">
import { toTypedSchema } from "@vee-validate/zod";
import { configure, useField, useFieldArray, useForm } from "vee-validate";
import { computed, inject, onBeforeUnmount, onMounted, ref } from "vue";
import * as z from "zod";

import {
  ArendetypInput,
  EmailInput,
  InternaKommentarer,
  KanalInput,
  KontaktorsakInput,
  KundgruppInput,
  LamnaOverInput,
  OmbudInput,
  SakraMeddelanden,
  SubmissionWarningDialog,
  TelefonnummerInput,
} from "@/components/form-components";
import CollapsableFormGroup from "@/components/ui/CollapsableFormGroup.vue";
import CopyToClipboard from "@/components/ui/CopyToClipboard.vue";
import FormGroup from "@/components/ui/FormGroup.vue";
import {
  type Anstalldarende,
  ArendeType,
  KanalType,
  KundgruppType,
  LamnaOverType,
} from "@/composables/KundserviceAppApiClient";
import { useApi } from "@/composables/useApi";
import { useNavigation } from "@/composables/useNavigation";
import { useConfigStore, useIndividStore } from "@/stores";
import { InternalCommentErrorMessage } from "@/common/constants";
import { composeAnstalldarendeUrl } from "@/common/utils/composeAnstalldArendeUrl";
import {
  mapPuzzelQueryStringToKanalType,
  mapPuzzelQueryStringToKundgruppType,
} from "@/common/mappers";
import { isValidPhoneNumber } from "@/common/validators";

import { useAnstalldarendeStore } from "../store";
import {
  AnstalldKanalOptions,
  AnstalldKundgruppOptions,
  AnstalldLamnaOverTillOptionGroups,
} from "./anstalldFormConstants";
import {
  shouldRequireAtLeastOneContact,
  shouldRequireInternKommentar,
  showSaveButton,
  showSaveCloseButton,
} from "./anstalldFormRules";

configure({
  validateOnChange: true,
  validateOnInput: true,
  validateOnBlur: false,
});

type ArendeFormProps = {
  arende?: Anstalldarende;
};

const props = defineProps<ArendeFormProps>();
const apiClient = useApi();
const navigation = useNavigation();
const configStore = useConfigStore();
const arendeStore = useAnstalldarendeStore();
const individStore = useIndividStore();

const scrollToTop = inject("scrollToTop", () => {});
const openArendenAndReload = inject("openArendenAndReload", () => {});

onMounted(() => {
  if (scrollToTop) {
    scrollToTop();
  }
});

onBeforeUnmount(() => {
  arendeStore.arende.error = null;
});

const zKontaktorsak = z.string().min(1, "En kontaktorsak måste väljas");
const zFortydligadKontaktorsak = z
  .string()
  .max(1000, "Får inte överskrida 1000 tecken")
  .nullable()
  .optional()
  .or(z.literal(""))
  .superRefine((value, ctx) => {
    if (
      kontaktorsakId.value === configStore.getKontaktorsakOvrigt?.id &&
      !value
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Förtydliga kontaktorsak",
      });
    }
  });
const zSamtalsamne = z.string();
const zSamtalsamnen = z
  .array(zSamtalsamne)
  .max(6, "Får inte överskrida 6 övriga samtalsämnen")
  .optional();
const zInternKommentar = z
  .string()
  .max(1000, "Får inte överskrida 1000 tecken")
  .optional()
  .superRefine((value, ctx) => {
    if (shouldRequireInternKommentar(values.lamnaOverTill) && !value) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: InternalCommentErrorMessage(values.lamnaOverTill),
      });
    }
  });
const zSakraMeddelanden = z
  .string()
  .max(5000, "Får inte överskrida 5000 tecken")
  .optional();

const zEmail = z
  .string()
  .email({ message: "E-postadressen är inte giltig" })
  .optional()
  .or(z.literal(""));

const zTelefonnummer = z
  .string()
  .optional()
  .superRefine((value, ctx) => {
    if (value && !isValidPhoneNumber(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Telefonnumret har inte ett giltigt format",
      });
    }
  });

const formSchema = toTypedSchema(
  z.object({
      kontaktorsakId: zKontaktorsak,
      fortydligadKontaktorsak: zFortydligadKontaktorsak,
      samtalsamneIds: zSamtalsamnen,
      internKommentar: zInternKommentar,
      sakraMeddelanden: zSakraMeddelanden,
      kanal: z.nativeEnum(KanalType),
      kundgrupp: z.nativeEnum(KundgruppType),
      ombud: z.boolean().optional(),
      email: zEmail,
      telefonnummer: zTelefonnummer,
      arendetyp: z.nativeEnum(ArendeType),
      close: z.boolean().optional(),
      lamnaOverTill: z.nativeEnum(LamnaOverType),
    })
    .superRefine((data, ctx) => {
      if (
        shouldRequireAtLeastOneContact(data.lamnaOverTill) &&
        !data.email &&
        !data.telefonnummer
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Ange minst en kontaktuppgift",
          path: ["atLeastOneContact"],
        });
      }
    }),
);

const { meta, values, handleSubmit, isSubmitting, validate, resetForm } =
  useForm({
    validationSchema: formSchema,
    initialValues: {
      kontaktorsakId: props.arende?.kontaktorsakId ?? "",
      fortydligadKontaktorsak: props.arende?.fortydligadKontaktorsak ?? "",
      samtalsamneIds: props.arende?.samtalsamneIds ?? [],
      internKommentar: "",
      sakraMeddelanden: "",
      kundgrupp:
        props.arende?.kundgrupp ??
        mapPuzzelQueryStringToKundgruppType(
          navigation.currentRoute.query.kundgrupp?.toString(),
        ) ??
        KundgruppType.Tjansteman,
      ombud: props.arende?.ombud ?? false,
      arendetyp: props.arende?.arendetyp ?? ArendeType.Kundservicearende,
      kanal:
        props.arende?.kanal ??
        mapPuzzelQueryStringToKanalType(
          navigation.currentRoute.query.mediakanal?.toString(),
        ),
      close: false,
      email: individStore.individ.data?.email ?? "",
      telefonnummer: individStore.individ.data?.telefonnummer ?? "",
      lamnaOverTill: LamnaOverType.Nej,
    },
  });

const { value: kontaktorsakId } = useField<string>("kontaktorsakId");
const { value: fortydligadKontaktorsak } = useField<string>(
  "fortydligadKontaktorsak",
);
const { remove: removeSamtalsamne, push: pushSamtalsamne } =
  useFieldArray<string>("samtalsamneIds");
const { value: internKommentar } = useField<string>("internKommentar");
const { value: sakraMeddelanden } = useField<string>("sakraMeddelanden");
const { value: kanal } = useField<KanalType>("kanal");
const { value: kundgrupp } = useField<KundgruppType>("kundgrupp");
const { value: email } = useField<string>("email");
const { value: telefonnummer } = useField<string>("telefonnummer");
const { value: arendetyp } = useField<ArendeType>("arendetyp");
const { setValue: setArendeCloseStatus } = useField<boolean>("close");
const { value: lamnaOverTill } = useField<LamnaOverType>("lamnaOverTill");
const { value: ombud } = useField<boolean>("ombud");

const saveButtonIsVisible = computed(() =>
  showSaveButton(values.lamnaOverTill, true),
);
const saveCloseButtonIsVisible = computed(() =>
  showSaveCloseButton(values.lamnaOverTill),
);

const showEmailIsMissingWarning = computed<boolean>(
  () => !values.email && !!values.sakraMeddelanden,
);

const showAtLeastOneContactMissingWarning = computed<boolean>(
  () =>
    shouldRequireAtLeastOneContact(values.lamnaOverTill) &&
    !values.email &&
    !values.telefonnummer,
);

const shouldUseDefaultTemplate = computed(() => {
  return props.arende?.kanal === KanalType.InloggadPaAvtalatSe && props.arende?.sakraMeddelanden.every((x) => x.createdByCustomer);
});

const showSubmissionWarningDialog = ref(false);
const showUnchangedDefaultMessageWarning = ref(false);
const showEmailMissingWarning = ref(false);
const hasUnchangedDefaultMessage = ref(false);

const onTemplateUnchanged = (value: boolean) => {
  hasUnchangedDefaultMessage.value = value;
};

const attemptSubmit = handleSubmit(async () => {
  const { valid } = await validate();
  if (!valid) {
    return;
  }

  showUnchangedDefaultMessageWarning.value = false;
  showEmailMissingWarning.value = false;

 if (hasUnchangedDefaultMessage.value || showEmailIsMissingWarning.value) {
    showUnchangedDefaultMessageWarning.value = hasUnchangedDefaultMessage.value;
    showEmailMissingWarning.value = showEmailIsMissingWarning.value;
    showSubmissionWarningDialog.value = true;
    return;
  }

  if (hasUnchangedDefaultMessage.value) {
    showUnchangedDefaultMessageWarning.value = true;
    showSubmissionWarningDialog.value = true;
    return;
  }

  if (showEmailIsMissingWarning.value) {
    showEmailMissingWarning.value = true;
    showSubmissionWarningDialog.value = true;
  } else {
    await onSubmit(); 
  }
});

const onSubmit = handleSubmit(async () => {
  showSubmissionWarningDialog.value = false;
  showUnchangedDefaultMessageWarning.value = false;
  showEmailMissingWarning.value = false;

  const fortydligadKontaktorsak =
    values.kontaktorsakId === configStore.getKontaktorsakOvrigt?.id
      ? values.fortydligadKontaktorsak
      : "";

  if (props.arende?.id) {
    await arendeStore.updateArende(
      {
        puzzelId:
          navigation.currentRoute.query.externalpuzzelid?.toString() ??
          undefined,
        individId: individStore.individ.data?.id as string,
        email: values.email,
        telefonnummer: values.telefonnummer,
        arendeId: props.arende?.id,
        arendetyp: values.arendetyp,
        internalNote: values.internKommentar,
        kontaktorsakId: values.kontaktorsakId,
        kontaktorsakNamn:
          configStore.getKontaktorsakById(values.kontaktorsakId)?.namn ?? "",
        fortydligadKontaktorsak: fortydligadKontaktorsak,
        samtalsamneIds: values.samtalsamneIds,
        conversation: values.sakraMeddelanden,
        isClosed: values.close,
        isVerified: individStore.verification.isVerified,
        isManuallyVerified: individStore.verification.isManuallyVerified,
        lamnaOverTill: values.lamnaOverTill,
        kundgrupp: values.kundgrupp,
      },
      apiClient,
    );

    if (!arendeStore.arende.error) {
      openArendenAndReload();
      resetForm();
      navigation.closeArendeView();
      await individStore.reloadIndivid(apiClient);
    }
  } else {
    const arende = await arendeStore.createArende(
      {
        puzzelId:
          navigation.currentRoute.query.externalpuzzelid?.toString() ??
          undefined,
        individId: individStore.individ.data?.id as string,
        email: values.email,
        telefonnummer: values.telefonnummer,
        kanal: values.kanal!,
        kundgrupp: values.kundgrupp,
        arendetyp: values.arendetyp,
        internalNote: values.internKommentar,
        kontaktorsakId: values.kontaktorsakId,
        kontaktorsakNamn:
          configStore.getKontaktorsakById(values.kontaktorsakId)?.namn ?? "",
        fortydligadKontaktorsak: values.fortydligadKontaktorsak,
        samtalsamneIds: values.samtalsamneIds,
        conversation: values.sakraMeddelanden,
        isClosed: values.close,
        isVerified: individStore.verification.isVerified,
        isManuallyVerified: individStore.verification.isManuallyVerified,
        lamnaOverTill: values.lamnaOverTill,
        ombud: values.ombud,
      },
      apiClient,
    );

    if (arende?.id) {
      await individStore.reloadIndivid(apiClient);
      resetForm();
      openArendenAndReload();
    }
  }
});
</script>
